import {
  ASPECT_KEYS,
  ASPECTS,
  type Aspect,
  buildPrompt,
  CAMERA_ANGLES,
  CAMERA_MOVES,
  type DirectorPlan,
  directorPlanSchema,
  type DirectorStateDto,
  frameSize,
  LANGUAGES,
  MAX_WORDS_PER_SECOND,
  MOVE_AMPLITUDES,
  MOVE_SPEEDS,
  type PlanShot,
  type PlanWarning,
  applySaferWording,
  planShotSchema,
  type PlanWorld,
  planToItems,
  type ProjectDto,
  type PromptContext,
  SHOT_SIZES,
  SIZE_KEYS,
  SIZES,
  spokenWords,
  type ShotData,
  type VideoSize,
} from '@studio/shared';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertTriangle,
  ArrowDown,
  ArrowUp,
  ChevronDown,
  ChevronRight,
  Clapperboard,
  Copy,
  Loader2,
  Plus,
  Save,
  Send,
  Trash2,
  Undo2,
  Wand2,
} from 'lucide-react';
import { type ReactNode, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { ProjectSettingsCard } from '@/components/project/ProjectSettingsCard';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { NativeSelect, ProgressBar, Switch, SwitchRow } from '@/components/ui/controls';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Field, Input, Label, Textarea } from '@/components/ui/form';
import { ErrorAlert } from '@/components/ui/misc';
import { api, errorMessage } from '@/lib/api';
import { useCharacters, useProducts } from '@/lib/queries';
import { cn, duration, money } from '@/lib/utils';
import { SectionTitle, useProjectCall, usePromptContext } from './common';

// ── small field helpers ─────────────────────────────────────────────────────

function T({
  label,
  value,
  onChange,
  rows = 2,
  hint,
  className,
  hindi,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  rows?: number;
  hint?: string;
  className?: string;
  hindi?: boolean;
}) {
  return (
    <div className={className}>
      <Label className="mb-1 block text-xs">
        {label}
        {hint && <span className="ml-1 font-normal text-muted-foreground">{hint}</span>}
      </Label>
      {rows <= 1 ? (
        <Input className={cn('h-8', hindi && 'hindi')} value={value} onChange={(e) => onChange(e.target.value)} />
      ) : (
        <Textarea
          className={cn('min-h-0 text-sm', hindi && 'hindi')}
          rows={rows}
          value={value}
          onChange={(e) => onChange(e.target.value)}
        />
      )}
    </div>
  );
}

function Sel<V extends string>({
  label,
  value,
  values,
  onChange,
  className,
}: {
  label: string;
  value: V;
  values: readonly V[];
  onChange: (v: V) => void;
  className?: string;
}) {
  return (
    <div className={className}>
      <Label className="mb-1 block text-xs">{label}</Label>
      <NativeSelect className="h-8" value={value} onChange={(e) => onChange(e.target.value as V)}>
        {values.map((v) => (
          <option key={v}>{v}</option>
        ))}
      </NativeSelect>
    </div>
  );
}

function Num({
  label,
  value,
  onChange,
  min,
  max,
  step = 1,
  className,
}: {
  label: string;
  value: number;
  onChange: (v: number) => void;
  min: number;
  max: number;
  step?: number;
  className?: string;
}) {
  return (
    <div className={className}>
      <Label className="mb-1 block text-xs">{label}</Label>
      <Input
        className="h-8"
        type="number"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(Math.min(max, Math.max(min, Number(e.target.value) || min)))}
      />
    </div>
  );
}

function Collapsible({
  title,
  children,
  defaultOpen = false,
  right,
}: {
  title: ReactNode;
  children: ReactNode;
  defaultOpen?: boolean;
  right?: ReactNode;
}) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <Card>
      <div className="flex items-center gap-2 p-3">
        <button
          type="button"
          className="flex flex-1 items-center gap-2 text-left font-medium"
          onClick={() => setOpen(!open)}
        >
          {open ? <ChevronDown className="size-4" /> : <ChevronRight className="size-4" />}
          {title}
        </button>
        {right}
      </div>
      {open && <div className="border-t p-4">{children}</div>}
    </Card>
  );
}

// ── the plan editor ─────────────────────────────────────────────────────────

function useDirectorWorld(project: ProjectDto, plan: DirectorPlan | null) {
  const { data: characters } = useCharacters();
  const base = usePromptContext(project, characters);
  return useMemo(() => {
    const lib = characters ?? [];
    const byName = new Map(lib.map((c) => [c.name.trim().toLowerCase(), c]));
    const ctx: PromptContext = { ...base, characters: { ...base.characters }, castIds: [...base.castIds] };
    // Characters the director invented are shown with their bible text (they are created on "Send").
    for (const c of plan?.characters ?? []) {
      const key = c.name.trim().toLowerCase();
      if (!byName.has(key)) {
        const id = `new:${key}`;
        ctx.characters[id] = {
          id,
          name: c.name,
          callName: '',
          visualDescription: c.appearance,
          voiceDescription: c.voice,
        };
      }
      const id = byName.get(key)?.id ?? `new:${key}`;
      if (!ctx.castIds.includes(id)) ctx.castIds.push(id);
    }
    const world: PlanWorld = {
      characterId: (name) => {
        const key = name.trim().toLowerCase();
        return (
          byName.get(key)?.id ??
          (plan?.characters.some((c) => c.name.trim().toLowerCase() === key) ? `new:${key}` : null)
        );
      },
      characterHasPicture: (id) => (lib.find((c) => c.id === id)?.images.length ?? 0) > 0,
      productId: project.product?.id ?? null,
      productHasPicture: !!project.product?.referenceImage,
      ctx,
    };
    return { world, inLibrary: (name: string) => byName.has(name.trim().toLowerCase()) };
  }, [characters, base, plan?.characters, project.product]);
}

type IndexedWarning = { w: PlanWarning; index: number };

/** Rule problems in the user's lines: the director keeps the line and suggests a safer wording; the team decides. */
function WarningList({
  items,
  onApply,
  onKeep,
}: {
  items: IndexedWarning[];
  onApply: (index: number) => void;
  onKeep: (index: number) => void;
}) {
  return (
    <div className="space-y-2">
      {items.map(({ w, index }) => {
        const done = w.status !== 'open';
        return (
          <div
            key={index}
            className={cn('rounded-md border p-3 text-sm', done ? 'opacity-60' : 'border-warning/50 bg-warning/5')}
          >
            <div className="flex flex-wrap items-center gap-2">
              <AlertTriangle className="size-4 text-warning" />
              <Badge variant={done ? 'secondary' : 'warning'}>{w.rule || 'rule'}</Badge>
              <span className="text-xs text-muted-foreground">
                {w.shotNumber ? `shot ${w.shotNumber}` : 'not in the plan'}
                {w.speaker ? ` · ${w.speaker}` : ''}
              </span>
              {w.status === 'applied' && <Badge variant="success">safer wording used</Badge>}
              {w.status === 'kept' && <Badge variant="secondary">kept as written</Badge>}
            </div>
            <div className="hindi mt-1.5">“{w.quote}”</div>
            {w.why && <div className="mt-1 text-xs text-muted-foreground">{w.why}</div>}
            {w.saferWording && (
              <div className="mt-2 text-xs">
                <span className="font-medium">Safer wording: </span>
                <span className="hindi text-sm">{w.saferWording}</span>
              </div>
            )}
            {!done && (
              <div className="mt-2 flex flex-wrap gap-2">
                {w.saferWording && (
                  <Button size="sm" onClick={() => onApply(index)}>
                    Use safer wording
                  </Button>
                )}
                <Button size="sm" variant="outline" onClick={() => onKeep(index)}>
                  Keep as written
                </Button>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function ShotEditorCard({
  shot,
  index,
  total,
  plan,
  prompt,
  onChange,
  onMove,
  onDuplicate,
  onDelete,
  warnings,
  onApplyWarning,
  onKeepWarning,
}: {
  warnings: IndexedWarning[];
  onApplyWarning: (index: number) => void;
  onKeepWarning: (index: number) => void;
  shot: PlanShot;
  index: number;
  total: number;
  plan: DirectorPlan;
  prompt: string | null;
  onChange: (s: PlanShot) => void;
  onMove: (d: -1 | 1) => void;
  onDuplicate: () => void;
  onDelete: () => void;
}) {
  const [open, setOpen] = useState(false);
  const set = <K extends keyof PlanShot>(k: K, v: PlanShot[K]) => onChange({ ...shot, [k]: v });
  const names = plan.characters.map((c) => c.name);
  const words = spokenWords(shot);
  const tooMany = shot.kind === 'shot' && words > shot.seconds * MAX_WORDS_PER_SECOND;
  const head =
    shot.kind === 'card'
      ? `Card: ${shot.cardText.replace(/\n/g, ' ')}`
      : `${shot.title || 'Untitled'} · ${shot.shotSize} ${shot.angle} · ${shot.lensMm} mm · ${shot.movement.type}`;
  return (
    <Card className={cn(open && 'ring-2 ring-primary/30')}>
      <div className="flex items-center gap-2 p-2.5">
        <button
          type="button"
          className="flex min-w-0 flex-1 items-center gap-2 text-left"
          onClick={() => setOpen(!open)}
        >
          {open ? <ChevronDown className="size-4 shrink-0" /> : <ChevronRight className="size-4 shrink-0" />}
          <span className="w-6 shrink-0 text-center text-sm font-semibold text-muted-foreground">{index + 1}</span>
          <span className="truncate text-sm font-medium">{head}</span>
          <Badge variant="outline">{shot.seconds} s</Badge>
          {shot.startFromPrevious && <Badge variant="secondary">continues</Badge>}
          {shot.lines.length > 0 && (
            <Badge variant="secondary">
              {shot.lines.length} line{shot.lines.length > 1 ? 's' : ''}
            </Badge>
          )}
          {warnings.some((x) => x.w.status === 'open') && (
            <Badge variant="warning">
              {warnings.filter((x) => x.w.status === 'open').length} line warning
              {warnings.filter((x) => x.w.status === 'open').length > 1 ? 's' : ''}
            </Badge>
          )}
          {tooMany && (
            <span title={`${words} words in ${shot.seconds} s`}>
              <AlertTriangle className="size-4 text-warning" />
            </span>
          )}
        </button>
        <Button
          variant="ghost"
          size="icon"
          className="size-8"
          disabled={index === 0}
          onClick={() => onMove(-1)}
          aria-label="Move up"
        >
          <ArrowUp />
        </Button>
        <Button
          variant="ghost"
          size="icon"
          className="size-8"
          disabled={index === total - 1}
          onClick={() => onMove(1)}
          aria-label="Move down"
        >
          <ArrowDown />
        </Button>
        <Button variant="ghost" size="icon" className="size-8" onClick={onDuplicate} aria-label="Duplicate">
          <Copy />
        </Button>
        <Button variant="ghost" size="icon" className="size-8 text-destructive" onClick={onDelete} aria-label="Delete">
          <Trash2 />
        </Button>
      </div>
      {open && (
        <div className="grid gap-5 border-t p-4 2xl:grid-cols-[minmax(0,1fr)_360px]">
          <div className="space-y-3">
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
              <Sel label="Kind" value={shot.kind} values={['shot', 'card'] as const} onChange={(v) => set('kind', v)} />
              <Num
                label="Seconds"
                value={shot.seconds}
                min={shot.kind === 'card' ? 1 : 4}
                max={15}
                step={0.5}
                onChange={(v) => set('seconds', v)}
              />
              <T
                label="Title"
                rows={1}
                value={shot.title}
                onChange={(v) => set('title', v)}
                className="sm:col-span-2"
              />
            </div>
            {shot.kind === 'card' ? (
              <T
                label="Card words (one row per line)"
                rows={3}
                value={shot.cardText}
                onChange={(v) => set('cardText', v)}
                hindi
              />
            ) : (
              <>
                <SectionTitle>Camera</SectionTitle>
                <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                  <Sel
                    label="Shot size"
                    value={shot.shotSize}
                    values={SHOT_SIZES}
                    onChange={(v) => set('shotSize', v)}
                  />
                  <Sel label="Angle" value={shot.angle} values={CAMERA_ANGLES} onChange={(v) => set('angle', v)} />
                  <Num label="Lens (mm)" value={shot.lensMm} min={8} max={400} onChange={(v) => set('lensMm', v)} />
                  <T
                    label="Depth of field"
                    rows={1}
                    value={shot.depthOfField}
                    onChange={(v) => set('depthOfField', v)}
                  />
                  <Sel
                    label="Movement"
                    value={shot.movement.type}
                    values={CAMERA_MOVES}
                    onChange={(v) => set('movement', { ...shot.movement, type: v })}
                  />
                  <Sel
                    label="Speed"
                    value={shot.movement.speed}
                    values={MOVE_SPEEDS}
                    onChange={(v) => set('movement', { ...shot.movement, speed: v })}
                  />
                  <Sel
                    label="Amplitude"
                    value={shot.movement.amplitude}
                    values={MOVE_AMPLITUDES}
                    onChange={(v) => set('movement', { ...shot.movement, amplitude: v })}
                  />
                  <T
                    label="Move detail"
                    rows={1}
                    value={shot.movement.description}
                    onChange={(v) => set('movement', { ...shot.movement, description: v })}
                  />
                </div>
                <SectionTitle hint="Write characters as [Name] and the product as [Product].">Scene</SectionTitle>
                <div className="grid gap-3 sm:grid-cols-2">
                  <div>
                    <Label className="mb-1 block text-xs">Location</Label>
                    <NativeSelect
                      className="h-8"
                      value={shot.location}
                      onChange={(e) => set('location', e.target.value)}
                    >
                      <option value="">—</option>
                      {plan.locations.map((l) => (
                        <option key={l.name}>{l.name}</option>
                      ))}
                      {shot.location && !plan.locations.some((l) => l.name === shot.location) && (
                        <option>{shot.location}</option>
                      )}
                    </NativeSelect>
                  </div>
                  <T label="Time of day" rows={1} value={shot.timeOfDay} onChange={(v) => set('timeOfDay', v)} />
                  <T
                    label="Blocking and action (first frame to last)"
                    rows={3}
                    value={shot.blocking}
                    onChange={(v) => set('blocking', v)}
                    className="sm:col-span-2"
                  />
                  <T label="Composition" value={shot.composition} onChange={(v) => set('composition', v)} />
                  <T label="Performance" value={shot.performance} onChange={(v) => set('performance', v)} />
                  <T label="Lighting" value={shot.lighting} onChange={(v) => set('lighting', v)} />
                  <T label="Colour grade" rows={1} value={shot.colorGrade} onChange={(v) => set('colorGrade', v)} />
                  <T label="Set dressing and props" value={shot.setDressing} onChange={(v) => set('setDressing', v)} />
                  <div>
                    <Label className="mb-1 block text-xs">In the shot</Label>
                    <div className="flex flex-wrap gap-1.5">
                      {names.map((n) => {
                        const on = shot.characters.includes(n);
                        return (
                          <button
                            key={n}
                            type="button"
                            onClick={() =>
                              set('characters', on ? shot.characters.filter((x) => x !== n) : [...shot.characters, n])
                            }
                            className={cn(
                              'rounded-full border px-2 py-0.5 text-xs',
                              on ? 'border-primary bg-primary/10 text-primary' : 'hover:bg-accent',
                            )}
                          >
                            {n}
                          </button>
                        );
                      })}
                      <label className="flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-xs">
                        <Switch checked={shot.productInShot} onCheckedChange={(v) => set('productInShot', v)} /> Product
                      </label>
                    </div>
                  </div>
                </div>
                <SectionTitle
                  hint={`Exact words, Devanagari for Hindi, no quotes. ${words} words — at most ${Math.floor(shot.seconds * MAX_WORDS_PER_SECOND)} for ${shot.seconds} s.`}
                >
                  Dialogue
                </SectionTitle>
                <div className="space-y-2">
                  {shot.lines.map((l, i) => {
                    const setLine = (patch: Partial<typeof l>) =>
                      set(
                        'lines',
                        shot.lines.map((x, j) => (j === i ? { ...x, ...patch } : x)),
                      );
                    return (
                      <div key={i} className="space-y-2 rounded-md border p-2">
                        <div className="grid gap-2 sm:grid-cols-[160px_1fr_1fr_auto]">
                          <NativeSelect
                            className="h-8"
                            value={l.speaker}
                            onChange={(e) => setLine({ speaker: e.target.value })}
                          >
                            {names.map((n) => (
                              <option key={n}>{n}</option>
                            ))}
                            {!names.includes(l.speaker) && <option>{l.speaker || 'A narrator'}</option>}
                          </NativeSelect>
                          <Input
                            className="h-8"
                            placeholder="says / shouts / whispers"
                            value={l.verb}
                            onChange={(e) => setLine({ verb: e.target.value })}
                          />
                          <Input
                            className="h-8"
                            placeholder="emotion, e.g. panicked"
                            value={l.emotion}
                            onChange={(e) => setLine({ emotion: e.target.value })}
                          />
                          <Button
                            variant="ghost"
                            size="icon"
                            className="size-8 text-destructive"
                            onClick={() =>
                              set(
                                'lines',
                                shot.lines.filter((_, j) => j !== i),
                              )
                            }
                            aria-label="Delete line"
                          >
                            <Trash2 />
                          </Button>
                        </div>
                        <Textarea
                          lang="hi"
                          className="hindi min-h-0"
                          rows={2}
                          value={l.text}
                          onChange={(e) => setLine({ text: e.target.value })}
                        />
                      </div>
                    );
                  })}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() =>
                      set('lines', [
                        ...shot.lines,
                        {
                          speaker: shot.characters[0] ?? names[0] ?? 'A narrator',
                          text: '',
                          verb: 'says',
                          emotion: '',
                          source: '',
                        },
                      ])
                    }
                  >
                    <Plus /> Add a line
                  </Button>
                </div>
                <SectionTitle>Sound and edit</SectionTitle>
                <div className="grid gap-3 sm:grid-cols-2">
                  <T label="Sound / ambience" value={shot.sound} onChange={(v) => set('sound', v)} />
                  <T label="Music cue" value={shot.music} onChange={(v) => set('music', v)} />
                  <T
                    label="Transition to the next shot"
                    rows={1}
                    value={shot.transition}
                    onChange={(v) => set('transition', v)}
                  />
                  <T label="Continuity notes" rows={1} value={shot.continuity} onChange={(v) => set('continuity', v)} />
                </div>
                <SectionTitle hint="Start from the previous shot's last frame, OR up to 3 reference pictures.">
                  Pictures
                </SectionTitle>
                <div className="flex flex-wrap items-center gap-4">
                  <label className="flex items-center gap-2 text-sm">
                    <Switch checked={shot.startFromPrevious} onCheckedChange={(v) => set('startFromPrevious', v)} />{' '}
                    Start from the previous shot's last frame
                  </label>
                  <label className="flex items-center gap-2 text-sm">
                    <Switch checked={shot.noOnScreenText} onCheckedChange={(v) => set('noOnScreenText', v)} /> No
                    on-screen text
                  </label>
                </div>
                {!shot.startFromPrevious && (
                  <div className="space-y-2">
                    {shot.referencePictures.map((r, i) => {
                      const setRef = (patch: Partial<typeof r>) =>
                        set(
                          'referencePictures',
                          shot.referencePictures.map((x, j) => (j === i ? { ...x, ...patch } : x)),
                        );
                      return (
                        <div key={i} className="grid gap-2 sm:grid-cols-[140px_1fr_auto]">
                          <NativeSelect
                            className="h-8"
                            value={r.type}
                            onChange={(e) => setRef({ type: e.target.value as typeof r.type })}
                          >
                            <option value="character">character photo</option>
                            <option value="product">product photo</option>
                            <option value="shot">still of a shot</option>
                          </NativeSelect>
                          {r.type === 'character' ? (
                            <NativeSelect
                              className="h-8"
                              value={r.name}
                              onChange={(e) => setRef({ name: e.target.value })}
                            >
                              <option value="">—</option>
                              {names.map((n) => (
                                <option key={n}>{n}</option>
                              ))}
                            </NativeSelect>
                          ) : r.type === 'shot' ? (
                            <NativeSelect
                              className="h-8"
                              value={r.shotNumber}
                              onChange={(e) => setRef({ shotNumber: Number(e.target.value) })}
                            >
                              <option value={0}>—</option>
                              {Array.from({ length: index }, (_, j) => (
                                <option key={j} value={j + 1}>
                                  last frame of shot {j + 1}
                                </option>
                              ))}
                            </NativeSelect>
                          ) : (
                            <div className="self-center text-sm text-muted-foreground">the product's label picture</div>
                          )}
                          <Button
                            variant="ghost"
                            size="icon"
                            className="size-8"
                            onClick={() =>
                              set(
                                'referencePictures',
                                shot.referencePictures.filter((_, j) => j !== i),
                              )
                            }
                            aria-label="Remove picture"
                          >
                            <Trash2 />
                          </Button>
                        </div>
                      );
                    })}
                    {shot.referencePictures.length < 3 && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() =>
                          set('referencePictures', [
                            ...shot.referencePictures,
                            { type: 'character', name: shot.characters[0] ?? '', shotNumber: 0 },
                          ])
                        }
                      >
                        <Plus /> Add a reference picture
                      </Button>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
          <div className="min-w-0">
            {warnings.length > 0 && (
              <div className="mb-4">
                <SectionTitle>Line warnings</SectionTitle>
                <WarningList items={warnings} onApply={onApplyWarning} onKeep={onKeepWarning} />
              </div>
            )}
            {prompt != null && (
              <>
                <SectionTitle hint="What FreeVideo will get (pictures are matched to the library when sent).">
                  Prompt preview
                </SectionTitle>
                <pre className="hindi max-h-[520px] overflow-auto rounded-md bg-muted p-3 text-xs leading-relaxed whitespace-pre-wrap">
                  {prompt}
                </pre>
                <div className="mt-1 text-xs text-muted-foreground">{prompt.length} characters</div>
              </>
            )}
          </div>
        </div>
      )}
    </Card>
  );
}

function PlanEditor({
  plan,
  onChange,
  project,
  inLibrary,
  prompts,
}: {
  plan: DirectorPlan;
  onChange: (p: DirectorPlan) => void;
  project: ProjectDto;
  inLibrary: (name: string) => boolean;
  prompts: Map<number, string>;
}) {
  const set = <K extends keyof DirectorPlan>(k: K, v: DirectorPlan[K]) => onChange({ ...plan, [k]: v });
  const look = plan.look;
  const setLook = <K extends keyof typeof look>(k: K, v: (typeof look)[K]) => set('look', { ...look, [k]: v });
  const setShot = (i: number, s: PlanShot) =>
    set(
      'shots',
      plan.shots.map((x, j) => (j === i ? s : x)),
    );
  const moveShot = (i: number, d: -1 | 1) => {
    const shots = [...plan.shots];
    [shots[i], shots[i + d]] = [shots[i + d]!, shots[i]!];
    set('shots', shots);
  };
  const total = plan.shots.reduce((n, s) => n + s.seconds, 0);
  const warnings: IndexedWarning[] = plan.warnings.map((w, index) => ({ w, index }));
  const open = warnings.filter((x) => x.w.status === 'open');
  const applyWarning = (index: number) => onChange(applySaferWording(plan, index));
  const keepWarning = (index: number) =>
    set(
      'warnings',
      plan.warnings.map((w, i) => (i === index ? { ...w, status: 'kept' as const } : w)),
    );
  return (
    <div className="space-y-3">
      {warnings.length > 0 && (
        <Card className={cn('p-4', open.length && 'border-warning/60')}>
          <div className="mb-1 font-medium">
            {open.length ? `${open.length} of your lines need a decision` : 'Your lines: every warning is decided'}
          </div>
          <p className="mb-3 text-xs text-muted-foreground">
            The director kept your dialogue word for word. These lines may break the claim rules: use the safer wording,
            or keep them as written — you decide.
          </p>
          <WarningList items={warnings} onApply={applyWarning} onKeep={keepWarning} />
        </Card>
      )}
      <Collapsible title={<span>Film: {plan.title || 'untitled'}</span>} defaultOpen>
        <div className="grid gap-3 sm:grid-cols-2">
          <T label="Title" rows={1} value={plan.title} onChange={(v) => set('title', v)} />
          <T label="Logline" value={plan.logline} onChange={(v) => set('logline', v)} />
          <T
            label="Story beats (one per line)"
            rows={4}
            value={plan.beats.join('\n')}
            onChange={(v) => set('beats', v.split('\n'))}
            className="sm:col-span-2"
          />
          <T label="Visual style" value={look.visualStyle} onChange={(v) => setLook('visualStyle', v)} />
          <T label="Lighting style" value={look.lighting} onChange={(v) => setLook('lighting', v)} />
          <T label="Lens kit" value={look.lensKit} onChange={(v) => setLook('lensKit', v)} />
          <T label="Camera language" value={look.cameraLanguage} onChange={(v) => setLook('cameraLanguage', v)} />
          <T label="Pacing and editing rhythm" value={look.pacing} onChange={(v) => setLook('pacing', v)} />
          <T label="Music direction" value={look.music} onChange={(v) => setLook('music', v)} />
          <T label="Sound design" value={look.soundDesign} onChange={(v) => setLook('soundDesign', v)} />
          <T label="Production design" value={look.productionDesign} onChange={(v) => setLook('productionDesign', v)} />
          <T label="Wardrobe" value={look.wardrobe} onChange={(v) => setLook('wardrobe', v)} />
          <div className="sm:col-span-2">
            <Label className="mb-1 block text-xs">Colour palette</Label>
            <div className="flex flex-wrap gap-2">
              {look.palette.map((c, i) => (
                <div key={i} className="flex items-center gap-1 rounded-md border p-1">
                  <input
                    type="color"
                    value={c.hex}
                    className="h-7 w-9 rounded"
                    onChange={(e) =>
                      setLook(
                        'palette',
                        look.palette.map((x, j) => (j === i ? { ...x, hex: e.target.value.toUpperCase() } : x)),
                      )
                    }
                  />
                  <Input
                    className="h-7 w-32 text-xs"
                    value={c.name}
                    onChange={(e) =>
                      setLook(
                        'palette',
                        look.palette.map((x, j) => (j === i ? { ...x, name: e.target.value } : x)),
                      )
                    }
                  />
                  <button
                    type="button"
                    className="px-1 text-muted-foreground"
                    onClick={() =>
                      setLook(
                        'palette',
                        look.palette.filter((_, j) => j !== i),
                      )
                    }
                    aria-label="Remove colour"
                  >
                    ×
                  </button>
                </div>
              ))}
              <Button
                variant="outline"
                size="sm"
                onClick={() => setLook('palette', [...look.palette, { name: 'New colour', hex: '#888888' }])}
              >
                <Plus /> Colour
              </Button>
            </div>
          </div>
        </div>
      </Collapsible>

      <Collapsible title={<span>Character bible ({plan.characters.length})</span>}>
        <p className="mb-3 text-xs text-muted-foreground">
          Each description goes word for word into every shot with that character. Characters not in the library are
          added to it when the plan is sent to the storyboard.
        </p>
        <div className="space-y-3">
          {plan.characters.map((c, i) => {
            const setC = (patch: Partial<typeof c>) =>
              set(
                'characters',
                plan.characters.map((x, j) => (j === i ? { ...x, ...patch } : x)),
              );
            return (
              <div key={i} className="grid gap-2 rounded-md border p-3 sm:grid-cols-[200px_1fr]">
                <div className="space-y-2">
                  <T label="Name" rows={1} value={c.name} onChange={(v) => setC({ name: v })} />
                  {inLibrary(c.name) ? (
                    <Badge variant="success">in the library</Badge>
                  ) : (
                    <Badge variant="warning">new — will be added</Badge>
                  )}
                  <T label="Role" rows={1} value={c.role} onChange={(v) => setC({ role: v })} />
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-destructive"
                    onClick={() =>
                      set(
                        'characters',
                        plan.characters.filter((_, j) => j !== i),
                      )
                    }
                  >
                    <Trash2 /> Remove
                  </Button>
                </div>
                <div className="space-y-2">
                  <T
                    label="Appearance (fixed)"
                    rows={4}
                    value={c.appearance}
                    onChange={(v) => setC({ appearance: v })}
                  />
                  <T label="Voice" rows={1} value={c.voice} onChange={(v) => setC({ voice: v })} />
                </div>
              </div>
            );
          })}
          <Button
            variant="outline"
            size="sm"
            onClick={() =>
              set('characters', [...plan.characters, { name: 'New character', role: '', appearance: '', voice: '' }])
            }
          >
            <Plus /> Add a character
          </Button>
        </div>
      </Collapsible>

      <Collapsible title={<span>Location bible ({plan.locations.length})</span>}>
        <div className="space-y-2">
          {plan.locations.map((l, i) => (
            <div key={i} className="grid gap-2 sm:grid-cols-[200px_1fr_auto]">
              <Input
                className="h-8"
                value={l.name}
                onChange={(e) =>
                  set(
                    'locations',
                    plan.locations.map((x, j) => (j === i ? { ...x, name: e.target.value } : x)),
                  )
                }
              />
              <Textarea
                className="min-h-0 text-sm"
                rows={2}
                value={l.description}
                onChange={(e) =>
                  set(
                    'locations',
                    plan.locations.map((x, j) => (j === i ? { ...x, description: e.target.value } : x)),
                  )
                }
              />
              <Button
                variant="ghost"
                size="icon"
                className="size-8 text-destructive"
                onClick={() =>
                  set(
                    'locations',
                    plan.locations.filter((_, j) => j !== i),
                  )
                }
                aria-label="Remove location"
              >
                <Trash2 />
              </Button>
            </div>
          ))}
          <Button
            variant="outline"
            size="sm"
            onClick={() => set('locations', [...plan.locations, { name: 'New location', description: '' }])}
          >
            <Plus /> Add a location
          </Button>
        </div>
      </Collapsible>

      <div className="flex items-center justify-between pt-2">
        <div className="text-sm font-medium">
          Shots ({plan.shots.length}, about {Math.round(total)} s)
        </div>
        <div className="text-xs text-muted-foreground">
          {project.language} dialogue · {frameSize(project.aspect, project.size).frame}
        </div>
      </div>
      <div className="space-y-2">
        {plan.shots.map((s, i) => (
          <ShotEditorCard
            key={i}
            shot={s}
            index={i}
            total={plan.shots.length}
            plan={plan}
            prompt={prompts.get(i + 1) ?? null}
            warnings={warnings.filter((x) => x.w.shotNumber === i + 1)}
            onApplyWarning={applyWarning}
            onKeepWarning={keepWarning}
            onChange={(x) => setShot(i, x)}
            onMove={(d) => moveShot(i, d)}
            onDuplicate={() =>
              set('shots', [...plan.shots.slice(0, i + 1), structuredClone(s), ...plan.shots.slice(i + 1)])
            }
            onDelete={() =>
              set(
                'shots',
                plan.shots.filter((_, j) => j !== i),
              )
            }
          />
        ))}
      </div>
      <div className="flex gap-2">
        <Button
          variant="outline"
          size="sm"
          onClick={() => set('shots', [...plan.shots, planShotSchema.parse({ title: 'New shot' })])}
        >
          <Plus /> Add shot
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={() =>
            set('shots', [...plan.shots, planShotSchema.parse({ kind: 'card', cardText: 'TEXT', seconds: 2.5 })])
          }
        >
          <Plus /> Add text card
        </Button>
      </div>
    </div>
  );
}

// ── the tab ─────────────────────────────────────────────────────────────────

export function DirectorTab({ project, onSent }: { project: ProjectDto; onSent: () => void }) {
  const qc = useQueryClient();
  const call = useProjectCall(project.id);
  const { data: products } = useProducts();
  const { data: characters } = useCharacters();
  const [planId, setPlanId] = useState<string | null>(null);
  const state = useQuery({
    queryKey: ['director', project.id, planId],
    queryFn: () => api<DirectorStateDto>(`/api/projects/${project.id}/director${planId ? `?planId=${planId}` : ''}`),
    refetchInterval: (q) => (q.state.data?.current?.status === 'running' ? 3000 : false),
  });
  const current = state.data?.current ?? null;

  // The brief and settings.
  const [brief, setBrief] = useState(project.script);
  const [targetSeconds, setTargetSeconds] = useState(45);
  const [tone, setTone] = useState('');
  const [keepDialogue, setKeepDialogue] = useState(true);
  const [productId, setProductId] = useState(project.productId ?? '');
  const [language, setLanguage] = useState(project.language);
  const [aspect, setAspect] = useState<Aspect>(project.aspect);
  const [size, setSize] = useState<VideoSize>(project.size ?? 'standard');
  const [castIds, setCastIds] = useState(project.castIds);
  const [starting, setStarting] = useState(false);
  useEffect(() => {
    if (current?.input) {
      setBrief((b) => b || current.input.brief);
      setTone((t) => t || current.input.tone);
      setTargetSeconds(current.input.targetSeconds || 45);
    }
  }, [current?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // The plan being edited.
  const [draft, setDraft] = useState<DirectorPlan | null>(null);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [sendOpen, setSendOpen] = useState(false);
  const [sending, setSending] = useState(false);
  useEffect(() => {
    if (!dirty) setDraft(current?.plan ? directorPlanSchema.parse(current.plan) : null);
  }, [current?.id, current?.status, current?.plan]); // eslint-disable-line react-hooks/exhaustive-deps
  const { world, inLibrary } = useDirectorWorld(project, draft);
  const prompts = useMemo(() => {
    const out = new Map<number, string>();
    if (!draft) return out;
    try {
      for (const item of planToItems(draft, world))
        if (item.kind === 'shot') out.set(item.number, buildPrompt(item.data as ShotData, world.ctx));
    } catch {
      /* preview only */
    }
    return out;
  }, [draft, world]);

  const elapsed = current?.status === 'running' ? Date.now() - Date.parse(current.createdAt) : 0;
  const [, tick] = useState(0);
  useEffect(() => {
    if (current?.status !== 'running') return;
    const t = setInterval(() => tick((n) => n + 1), 1000);
    return () => clearInterval(t);
  }, [current?.status]);

  const direct = async () => {
    setStarting(true);
    const saved = await call(`/api/projects/${project.id}`, {
      method: 'PATCH',
      body: { productId: productId || null, language, aspect, size, castIds, script: brief },
    });
    if (saved) {
      try {
        const s = await api<DirectorStateDto>(`/api/projects/${project.id}/director`, {
          method: 'POST',
          body: { brief, targetSeconds, tone, keepDialogue },
        });
        setDirty(false);
        setPlanId(null);
        qc.setQueryData(['director', project.id, null], s);
      } catch (err) {
        toast.error(errorMessage(err));
      }
    }
    setStarting(false);
  };

  const save = async () => {
    if (!current || !draft) return;
    setSaving(true);
    try {
      await api(`/api/director/${current.id}`, { method: 'PATCH', body: { plan: draft } });
      setDirty(false);
      await qc.invalidateQueries({ queryKey: ['director', project.id] });
      toast.success('Plan saved');
    } catch (err) {
      toast.error(errorMessage(err));
    }
    setSaving(false);
  };

  const send = async (mode: 'replace' | 'append') => {
    if (!current) return;
    setSending(true);
    if (dirty) await save();
    const res = await call(`/api/director/${current.id}/send`, { method: 'POST', body: { mode } });
    setSending(false);
    setSendOpen(false);
    if (res) {
      toast.success('The plan is in the storyboard — generate the shots there');
      void qc.invalidateQueries({ queryKey: ['characters'] });
      void qc.invalidateQueries({ queryKey: ['director', project.id] });
      onSent();
    }
  };

  const usage = current?.usage;
  return (
    <div className="grid gap-6 xl:grid-cols-[380px_minmax(0,1fr)]">
      <div className="space-y-4">
        <ProjectSettingsCard project={project} />
        <Card>
          <CardHeader>
            <CardTitle>Brief for the director</CardTitle>
            <CardDescription>
              An idea, a brief or a full script. ChatGPT plans every shot like a film director: camera, lens, movement,
              lighting, the characters' exact look, dialogue and sound.
            </CardDescription>
          </CardHeader>
          <CardContent className="grid gap-3">
            <Textarea
              rows={10}
              className="hindi"
              value={brief}
              onChange={(e) => setBrief(e.target.value)}
              placeholder="e.g. A funny 40-second ad: the liver complains about oily food…"
            />
            <div className="grid grid-cols-2 gap-3">
              <Field label="Length (seconds)">
                {(p) => (
                  <Input
                    {...p}
                    type="number"
                    min={10}
                    max={180}
                    value={targetSeconds}
                    onChange={(e) => setTargetSeconds(Number(e.target.value) || 45)}
                  />
                )}
              </Field>
              <Field label="Language">
                {(p) => (
                  <NativeSelect {...p} value={language} onChange={(e) => setLanguage(e.target.value)}>
                    {LANGUAGES.map((l) => (
                      <option key={l}>{l}</option>
                    ))}
                  </NativeSelect>
                )}
              </Field>
              <Field label="Shape">
                {(p) => (
                  <NativeSelect {...p} value={aspect} onChange={(e) => setAspect(e.target.value as Aspect)}>
                    {ASPECT_KEYS.map((a) => (
                      <option key={a} value={a}>
                        {ASPECTS[a].label}
                      </option>
                    ))}
                  </NativeSelect>
                )}
              </Field>
              <Field label="Size" hint={SIZES[size].hint}>
                {(p) => (
                  <NativeSelect {...p} value={size} onChange={(e) => setSize(e.target.value as VideoSize)}>
                    {SIZE_KEYS.map((k) => (
                      <option key={k} value={k}>
                        {SIZES[k].label}
                      </option>
                    ))}
                  </NativeSelect>
                )}
              </Field>
            </div>
            <Field label="Product">
              {(p) => (
                <NativeSelect {...p} value={productId} onChange={(e) => setProductId(e.target.value)}>
                  <option value="">— none —</option>
                  {products?.map((x) => (
                    <option key={x.id} value={x.id}>
                      {x.title}
                    </option>
                  ))}
                </NativeSelect>
              )}
            </Field>
            <div>
              <div className="mb-1.5 text-sm font-medium">Characters</div>
              <div className="flex flex-wrap gap-1.5">
                {characters?.map((c) => {
                  const on = castIds.includes(c.id);
                  return (
                    <button
                      key={c.id}
                      type="button"
                      onClick={() => setCastIds(on ? castIds.filter((x) => x !== c.id) : [...castIds, c.id])}
                      className={cn(
                        'rounded-full border px-2 py-0.5 text-xs',
                        on ? 'border-primary bg-primary/10 text-primary' : 'hover:bg-accent',
                      )}
                    >
                      {c.name}
                    </button>
                  );
                })}
              </div>
              <p className="mt-1 text-xs text-muted-foreground">
                The director may also invent new characters; they join the library when you send the plan.
              </p>
            </div>
            <SwitchRow
              label="Keep my dialogue exactly"
              description="On: every line of your script is used word for word (only written in Devanagari); rule problems are shown as warnings for you to decide. Off: the director may rewrite the lines."
              checked={keepDialogue}
              onCheckedChange={setKeepDialogue}
            />
            <Field label="Tone and style hints">
              {(p) => (
                <Input
                  {...p}
                  value={tone}
                  onChange={(e) => setTone(e.target.value)}
                  placeholder="e.g. funny, fast, bright colours, warm ending"
                />
              )}
            </Field>
            <Button
              onClick={() => void direct()}
              loading={starting}
              disabled={current?.status === 'running' || !state.data}
            >
              <Wand2 /> {current?.status === 'done' ? 'Direct again (new version)' : 'Direct'}
            </Button>
            {state.data && (
              <p className="text-xs text-muted-foreground">
                {state.data.configured
                  ? `Model ${state.data.model}, thinking: ${state.data.reasoning}. Usually 1–4 minutes; you can leave this page.`
                  : 'No OpenAI key on this server: the director returns a sample plan (fake mode only).'}
              </p>
            )}
          </CardContent>
        </Card>
      </div>

      <div className="min-w-0 space-y-4">
        {state.data && state.data.versions.length > 0 && (
          <div className="flex flex-wrap items-center gap-2">
            <NativeSelect
              className="w-auto"
              value={current?.id ?? ''}
              onChange={(e) => setPlanId(e.target.value || null)}
            >
              {state.data.versions.map((v) => (
                <option key={v.id} value={v.id}>
                  Version {v.version} — {v.status}
                  {v.edited ? ', edited' : ''}
                  {v.sentAt ? ', sent' : ''}
                </option>
              ))}
            </NativeSelect>
            {usage && (
              <span className="text-xs text-muted-foreground tabular">
                {usage.model} · {usage.inputTokens.toLocaleString('en-IN')} in /{' '}
                {usage.outputTokens.toLocaleString('en-IN')} out
                {usage.reasoningTokens ? ` (${usage.reasoningTokens.toLocaleString('en-IN')} thinking)` : ''} · ≈{' '}
                {money(usage.costUsd, 3)} · {duration(usage.seconds * 1000)}
              </span>
            )}
            {current?.status === 'done' && draft && (
              <div className="ml-auto flex gap-2">
                {dirty && (
                  <Button
                    variant="ghost"
                    onClick={() => {
                      setDirty(false);
                      setDraft(current.plan ? directorPlanSchema.parse(current.plan) : null);
                    }}
                  >
                    <Undo2 /> Discard changes
                  </Button>
                )}
                <Button variant="outline" onClick={() => void save()} loading={saving} disabled={!dirty}>
                  <Save /> Save changes
                </Button>
                <Button
                  onClick={() => (project.shots.length ? setSendOpen(true) : void send('replace'))}
                  loading={sending}
                >
                  <Send /> Send to storyboard
                </Button>
              </div>
            )}
          </div>
        )}

        {state.error && <ErrorAlert message={errorMessage(state.error)} />}
        {!state.isLoading && !current && (
          <Card className="grid place-items-center p-12 text-center text-sm text-muted-foreground">
            <Clapperboard className="mb-2 size-8" />
            Write the brief and press <b>Direct</b>. The plan appears here, fully editable.
          </Card>
        )}
        {current?.status === 'running' && (
          <Card className="space-y-3 p-6">
            <div className="flex items-center gap-2 font-medium">
              <Loader2 className="size-4 animate-spin" /> The director is planning the film… ({duration(elapsed)})
            </div>
            <ProgressBar value={Math.min(0.95, elapsed / 180_000)} />
            <p className="text-sm text-muted-foreground">
              This usually takes 1–4 minutes. It keeps working if you leave or refresh the page.
            </p>
          </Card>
        )}
        {current?.status === 'failed' && (
          <ErrorAlert title="The director could not finish" message={current.error ?? 'Unknown error'} />
        )}
        {current?.status === 'done' && draft && (
          <PlanEditor
            plan={draft}
            project={project}
            inLibrary={inLibrary}
            prompts={prompts}
            onChange={(p) => {
              setDraft(p);
              setDirty(true);
            }}
          />
        )}
      </div>

      <Dialog open={sendOpen} onOpenChange={setSendOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Send the plan to the storyboard</DialogTitle>
            <DialogDescription>
              The project already has {project.shots.length} shots and cards. Replace them (their videos are removed
              too), or add the director's shots after them?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSendOpen(false)}>
              Cancel
            </Button>
            <Button variant="outline" onClick={() => void send('append')} loading={sending}>
              Add after
            </Button>
            <Button variant="destructive" onClick={() => void send('replace')} loading={sending}>
              Replace
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
